// src/components/negocios/MatchCard.jsx
import React, { useMemo } from 'react';
import {
  Home,
  Car,
  Bike,
  MapPin,
  ArrowRight,
  Sparkles,
  MessageCircle,
  GitMerge,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Coins,
} from 'lucide-react';

const AURORA = {
  emerald: '#34D399',
  purple: '#A78BFA',
  amber: '#F59E0B',
  border: 'rgba(255,255,255,0.10)',
  text: 'rgba(24,24,27,0.92)', // zinc-900
  text2: 'rgba(24,24,27,0.65)',
  text3: 'rgba(24,24,27,0.45)',
};

function toNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function moneyBRL(v) {
  const n = toNumber(v, 0);
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

function shortText(str = '', max = 26) {
  const s = String(str || '');
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

function iconForSubtipo(subtipo = '') {
  const s = String(subtipo || '').toLowerCase();
  if (s.includes('casa') || s.includes('sobrado')) return Home;
  if (s.includes('apto') || s.includes('apart')) return Home;
  if (s.includes('terreno') || s.includes('lote') || s.includes('area'))
    return MapPin;
  if (s.includes('carro') || s.includes('caminhonete') || s.includes('veiculo'))
    return Car;
  if (s.includes('moto')) return Bike;
  return Sparkles;
}

function statusBadge(status = 'AMARELO') {
  const s = String(status || '').toUpperCase();
  if (s === 'VERDE') {
    return {
      label: 'Match forte',
      icon: CheckCircle2,
      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    };
  }
  if (s === 'VERMELHO') {
    return {
      label: 'Inviável',
      icon: XCircle,
      cls: 'bg-rose-50 text-rose-700 border-rose-200',
    };
  }
  return {
    label: 'Precisa ajuste',
    icon: AlertTriangle,
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
  };
}

function pickAnchorItem(itens = []) {
  if (!Array.isArray(itens) || itens.length === 0) return null;

  // Prioriza IMOVEL, senão pega maior valor_troca
  const imoveis = itens.filter(
    (x) => String(x?.tipo || '').toUpperCase() === 'IMOVEL'
  );
  const base = imoveis.length ? imoveis : itens;

  return base
    .slice()
    .sort(
      (a, b) =>
        toNumber(b?.valor_troca ?? b?.valor_referencia, 0) -
        toNumber(a?.valor_troca ?? a?.valor_referencia, 0)
    )[0];
}

function pickSmallItems(itens = [], anchorId) {
  const rest = (Array.isArray(itens) ? itens : []).filter(
    (x) => x?.id !== anchorId
  );

  // Tenta priorizar ATIVO, e por valor
  const ativos = rest.filter(
    (x) => String(x?.tipo || '').toUpperCase() === 'ATIVO'
  );
  const base = ativos.length ? ativos : rest;

  return base
    .slice()
    .sort(
      (a, b) =>
        toNumber(b?.valor_troca ?? b?.valor_referencia, 0) -
        toNumber(a?.valor_troca ?? a?.valor_referencia, 0)
    )
    .slice(0, 2);
}

function buildResumo(match) {
  const tipo = String(match?.tipo_match || '').toUpperCase();
  const torna = toNumber(match?.torna?.total ?? match?.torna_total ?? 0, 0);

  const just = Array.isArray(match?.justificativas) ? match.justificativas : [];
  const pend = Array.isArray(match?.pendencias) ? match.pendencias : [];
  const motivos = just.length ? just : [];

  if (tipo === 'TRIANGULAR') {
    return {
      l1: `Triangular com pivô para destravar o aceite.`,
      l2:
        torna > 0
          ? `Torna total estimada: ${moneyBRL(torna)}.`
          : `Torna estimada: sem diferença relevante.`,
      l3: pend.length
        ? `Pendências: ${shortText(pend[0]?.mensagem || pend[0], 42)}`
        : motivos[0]
          ? shortText(motivos[0], 42)
          : 'Fluxo pronto para conversar.',
    };
  }

  return {
    l1: `Direto: troca + complemento para fechar.`,
    l2:
      torna > 0
        ? `Torna estimada: ${moneyBRL(torna)}.`
        : `Torna estimada: sem diferença relevante.`,
    l3: pend.length
      ? `Pendências: ${shortText(pend[0]?.mensagem || pend[0], 42)}`
      : motivos[0]
        ? shortText(motivos[0], 42)
        : 'Fluxo pronto para conversar.',
  };
}

function FlowArrows({ tipo = 'DIRETO' }) {
  const t = String(tipo || '').toUpperCase();
  // SVG simples (duas setas curvas). Mantém discreto.
  return (
    <svg
      className="pointer-events-none absolute inset-0"
      viewBox="0 0 1000 220"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <marker
          id="arrowHead"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="5"
          orient="auto"
        >
          <path d="M0,0 L10,5 L0,10 Z" fill="rgba(16,185,129,0.55)" />
        </marker>
      </defs>

      {/* Direto: B -> A */}
      {t !== 'TRIANGULAR' && (
        <path
          d="M520,125 C430,70 330,60 235,95"
          fill="none"
          stroke="rgba(16,185,129,0.55)"
          strokeWidth="2.5"
          markerEnd="url(#arrowHead)"
        />
      )}

      {/* Triangular: B -> C e C -> A */}
      {t === 'TRIANGULAR' && (
        <>
          <path
            d="M520,85 C560,50 640,50 690,85"
            fill="none"
            stroke="rgba(167,139,250,0.55)"
            strokeWidth="2.5"
            markerEnd="url(#arrowHead)"
          />
          <path
            d="M690,125 C560,190 410,190 235,125"
            fill="none"
            stroke="rgba(16,185,129,0.55)"
            strokeWidth="2.5"
            markerEnd="url(#arrowHead)"
          />
        </>
      )}
    </svg>
  );
}

/**
 * MatchCard (Horizontal)
 *
 * Props:
 * - match: objeto MatchResult do seu motor (simulator.js)
 * - onOpen(match): abrir modal/detalhes
 * - onChat(match): ação "Conversar"
 * - className: extra classes
 */
export default function MatchCard({ match, onOpen, onChat, className = '' }) {
  const vm = useMemo(() => {
    const itens = Array.isArray(match?.itens) ? match.itens : [];
    const anchor = pickAnchorItem(itens);
    const small = pickSmallItems(itens, anchor?.id);

    const tipo = String(
      match?.tipo_match || match?.tipo || 'DIRETO'
    ).toUpperCase();
    const status = String(match?.status || 'AMARELO').toUpperCase();
    const score = toNumber(match?.score_match ?? match?.score ?? 0, 0);
    const torna = toNumber(match?.torna?.total ?? match?.torna_total ?? 0, 0);

    const resumo = buildResumo(match);

    return {
      tipo,
      status,
      score,
      torna,
      anchor,
      small1: small[0] || null,
      small2: small[1] || null,
      resumo,
    };
  }, [match]);

  const badge = statusBadge(vm.status);
  const BadgeIcon = badge.icon;

  const AnchorIcon = iconForSubtipo(
    vm.anchor?.subtipo || vm.anchor?.tipo || ''
  );
  const Small1Icon = iconForSubtipo(
    vm.small1?.subtipo || vm.small1?.tipo || ''
  );
  const Small2Icon = iconForSubtipo(
    vm.small2?.subtipo || vm.small2?.tipo || ''
  );

  const tipoLabel =
    vm.tipo === 'TRIANGULAR' ? 'Triangular (3 partes)' : 'Direto (2 partes)';

  return (
    <div
      className={[
        'relative overflow-hidden rounded-2xl border bg-white shadow-sm',
        'p-4',
        className,
      ].join(' ')}
    >
      {/* Top bar */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-medium text-zinc-700">
            <GitMerge className="h-3.5 w-3.5" />
            {tipoLabel}
          </span>

          <span
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold ${badge.cls}`}
          >
            <BadgeIcon className="h-3.5 w-3.5" />
            {badge.label}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] font-semibold text-zinc-800">
            <Sparkles className="h-3.5 w-3.5" />
            Score: {Math.round(vm.score)}
          </span>

          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-800">
            <Coins className="h-3.5 w-3.5" />
            Torna: {moneyBRL(vm.torna)}
          </span>
        </div>
      </div>

      {/* Body grid */}
      <div className="relative">
        {/* setas (desktop) */}
        <div className="hidden lg:block">
          <FlowArrows tipo={vm.tipo} />
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[360px_260px_minmax(0,1fr)]">
          {/* COL A: Anchor (grande) */}
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white text-emerald-700 shadow-sm">
                  <AnchorIcon className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold text-emerald-700">
                    ITEM ÂNCORA (MAIOR VALOR)
                  </div>
                  <div className="truncate text-sm font-extrabold text-zinc-900">
                    {shortText(vm.anchor?.subtipo || 'Imóvel', 32)}
                  </div>
                </div>
              </div>

              <span className="inline-flex rounded-full bg-white/90 px-3 py-1 text-xs font-extrabold text-zinc-900 shadow-sm">
                {moneyBRL(
                  vm.anchor?.valor_troca ??
                    vm.anchor?.valor_referencia ??
                    vm.anchor?.valor_venda
                )}
              </span>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-zinc-600">
              {(vm.anchor?.metadata?.bairro || vm.anchor?.bairro) && (
                <span className="inline-flex items-center gap-1 rounded-full bg-white/80 px-2 py-1">
                  <MapPin className="h-3 w-3" />
                  {shortText(
                    vm.anchor?.metadata?.bairro || vm.anchor?.bairro,
                    24
                  )}
                </span>
              )}

              {(vm.anchor?.metadata?.cidade || vm.anchor?.cidade) && (
                <span className="inline-flex items-center gap-1 rounded-full bg-white/80 px-2 py-1">
                  {shortText(
                    vm.anchor?.metadata?.cidade || vm.anchor?.cidade,
                    18
                  )}
                </span>
              )}

              <span className="inline-flex items-center rounded-full bg-white/80 px-2 py-1 font-semibold text-emerald-700">
                A (Alvo)
              </span>

              {vm.anchor?.metadata?.aceita_permuta && (
                <span className="inline-flex items-center rounded-full bg-white/80 px-2 py-1 font-semibold text-emerald-700">
                  Aceita permuta
                </span>
              )}
            </div>
          </div>

          {/* COL B: 2 cards menores */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
            {/* Small 1 (B) */}
            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
              <div className="flex items-start justify-between gap-2">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white text-zinc-700 shadow-sm">
                  <Small1Icon className="h-4.5 w-4.5" />
                </span>
                <span className="inline-flex rounded-full bg-white px-2 py-1 text-[11px] font-bold text-zinc-800">
                  B
                </span>
              </div>

              <div className="mt-2 text-xs font-extrabold text-zinc-900">
                {vm.small1
                  ? shortText(vm.small1.subtipo || 'Ativo', 18)
                  : 'Sem item B'}
              </div>
              <div className="mt-1 text-[11px] font-semibold text-emerald-700">
                {vm.small1
                  ? moneyBRL(
                      vm.small1.valor_troca ?? vm.small1.valor_referencia
                    )
                  : '—'}
              </div>

              {!vm.small1 && (
                <div className="mt-2 text-[11px] text-zinc-500">
                  Selecione/registre o item de troca do cliente.
                </div>
              )}
            </div>

            {/* Small 2 (C) */}
            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
              <div className="flex items-start justify-between gap-2">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white text-zinc-700 shadow-sm">
                  <Small2Icon className="h-4.5 w-4.5" />
                </span>
                <span className="inline-flex rounded-full bg-white px-2 py-1 text-[11px] font-bold text-zinc-800">
                  C
                </span>
              </div>

              <div className="mt-2 text-xs font-extrabold text-zinc-900">
                {vm.small2
                  ? shortText(vm.small2.subtipo || 'Pivô', 18)
                  : vm.tipo === 'TRIANGULAR'
                    ? 'Sem pivô C'
                    : 'Pivô opcional'}
              </div>
              <div className="mt-1 text-[11px] font-semibold text-purple-700">
                {vm.small2
                  ? moneyBRL(
                      vm.small2.valor_troca ?? vm.small2.valor_referencia
                    )
                  : '—'}
              </div>

              {!vm.small2 && vm.tipo === 'TRIANGULAR' && (
                <div className="mt-2 text-[11px] text-zinc-500">
                  Falta definir o item pivô pra destravar o aceite.
                </div>
              )}

              {!vm.small2 && vm.tipo !== 'TRIANGULAR' && (
                <div className="mt-2 text-[11px] text-zinc-500">
                  Se precisar, busque um pivô para triangular.
                </div>
              )}
            </div>
          </div>

          {/* COL C: explicação + ações */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-sm font-extrabold text-zinc-900">
                Como esse match fecha
              </div>
              <span className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] font-semibold text-zinc-800">
                {vm.tipo === 'TRIANGULAR' ? (
                  <>
                    <GitMerge className="h-3.5 w-3.5" />B → C → A
                  </>
                ) : (
                  <>
                    <ArrowRight className="h-3.5 w-3.5" />B → A
                  </>
                )}
              </span>
            </div>

            <div className="space-y-1 text-[12px] text-zinc-700">
              <div className="font-semibold">{vm.resumo.l1}</div>
              <div className="text-zinc-600">{vm.resumo.l2}</div>
              <div className="text-zinc-600">{vm.resumo.l3}</div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onOpen?.(match)}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-emerald-700 transition"
              >
                Ver detalhes
                <ArrowRight className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={() => onChat?.(match)}
                className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-xs font-bold text-zinc-800 hover:bg-zinc-50 transition"
              >
                Conversar
                <MessageCircle className="h-4 w-4" />
              </button>

              {/* mini indicador */}
              <span className="ml-auto inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-2 text-[11px] font-semibold text-emerald-800">
                <Sparkles className="h-3.5 w-3.5" />
                {vm.tipo === 'TRIANGULAR'
                  ? 'Estratégia: pivô'
                  : 'Estratégia: direto'}
              </span>
            </div>

            {/* detalhes extras (opcional) */}
            <div className="mt-3 text-[11px] text-zinc-500">
              Dica: clique em{' '}
              <span className="font-semibold">Ver detalhes</span> para abrir
              transfers, saldos e pendências.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
